import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StructuredData from "@/components/feature/StructuredData";
import { reviews, faqs } from "@/mocks/movies";

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const contactFormUrl = "https://readdy.ai/api/form/d9od35m8mbnljin5abjg";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("loading");
    setFormError("");

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim() || "";

    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete("phone_alt");

      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        urlParams.append(key, value as string);
      });

      const response = await fetch(contactFormUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes("spam")) {
          setFormStatus("error");
          setFormError("提交失败，请稍后再试");
        } else {
          setFormStatus("error");
          setFormError(serverMsg || "提交失败，请稍后再试");
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请稍后再试");
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "天美麻花星空影视",
    url: "https://www.tianmeimahua.com",
    description: "天美麻花星空影视 - 超清蓝光电视剧全集、电影大全不缓冲免费在线观看，大屏TV版最新综艺动漫免费看",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@tianmeimahua.com",
      availableLanguage: ["Chinese"],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <StructuredData organizationSchema={organizationSchema} faqSchema={faqSchema} />
      <Navbar />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "关于我们" },
          ]}
        />
      </div>

      <main className="w-full px-4 md:px-8 py-6 md:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <section className="text-center mb-12 md:mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于天美麻花星空影视
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              天美麻花星空影视是一个致力于为全球华人提供免费高清影视内容的在线平台。
              我们汇集了海量超清蓝光电视剧全集、电影大全，以及最新综艺和热门动漫，
              让您随时随地享受不缓冲的流畅观影体验。
            </p>
          </section>

          {/* Brand Story */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              品牌故事
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                天美麻花星空影视创立于热爱影视的初衷。在这个信息爆炸的时代，我们希望为每一位影视爱好者打造一个纯净、高效的观影平台。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                我们深知，好的观影体验不仅需要丰富的片源，更需要流畅的播放和清晰的画质。
                因此，天美麻花星空影视采用了先进的CDN加速技术和智能码率自适应方案，
                确保无论您使用什么设备、身处何种网络环境，都能享受稳定流畅的超清蓝光观影体验。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                从手机到平板，从电脑到大屏TV，天美麻花星空影视覆盖了所有主流观看设备。
                我们持续更新片源库，第一时间同步热播剧集和新上映电影，让您永远不会错过精彩内容。
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  icon: "ri-4k-line",
                  title: "超清蓝光画质",
                  desc: "支持1080P到4K超清画质，每一帧都清晰细腻，带来影院级视觉享受。",
                },
                {
                  icon: "ri-speed-up-line",
                  title: "流畅不缓冲",
                  desc: "先进的CDN加速和自适应码率技术，智能匹配网络环境，告别卡顿和缓冲。",
                },
                {
                  icon: "ri-smartphone-line",
                  title: "多设备支持",
                  desc: "手机、平板、电脑、大屏TV全覆盖，随时随地畅享影视盛宴。",
                },
                {
                  icon: "ri-refresh-line",
                  title: "每日更新",
                  desc: "专业内容团队全天候更新，热门剧集和电影第一时间同步上线。",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "完全免费",
                  desc: "无需注册，无需付费，所有内容免费观看，让好内容触手可及。",
                },
                {
                  icon: "ri-global-line",
                  title: "片源丰富",
                  desc: "涵盖电视剧、电影、综艺、动漫等全品类，满足各类观影需求。",
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/60"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${feature.icon} text-lg text-primary-500`} />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reviews */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${
                          i < review.rating ? "ri-star-fill text-accent-500" : "ri-star-fill text-foreground-300"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.content}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground-800">{review.name}</span>
                    <span className="text-xs text-foreground-400">{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-lg bg-background-100 border border-background-200/60 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-heading font-semibold text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0 ${
                        openFaq === idx ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openFaq === idx && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="max-w-lg mx-auto bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60">
              {formStatus === "success" ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-primary-500" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">提交成功</h3>
                  <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您！</p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-4 px-6 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      />
                    </div>
                    <div className="visually-hidden-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    {formStatus === "error" && formError && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === "loading"}
                      className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === "loading" ? "提交中..." : "发送留言"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}